import React, { useEffect, useState } from 'react';
import { Linking } from 'react-native';
import { useRouter, useGlobalSearchParams } from 'expo-router';
import styled from '@emotion/styled';
import { useConnectionState } from '@sendbird/uikit-react/hooks/useConnectionState';
import { ConnectionState } from '@sendbird/chat';
import { GroupChannelListOrder } from '@sendbird/chat/groupChannel';
import { useSendbird } from '@sendbird/uikit-react';

import ChannelList from './ChannelList/ChannelList.web';
import Channel from '@/components/messages/Channel/Channel.web';
import ChannelSettings from '@sendbird/uikit-react/ChannelSettings';
import { lightGrey, sand, white } from '@/shared/styles/colors';
import {
  ErrorContainer,
  StyledBodyText,
  StyledH3,
  SupportLink,
} from '@/components/messages/styledComponents';
import { EMME_SUPPORT_EMAIL } from '@/shared/constants/utility';
import useBreakpoint from '@/shared/hooks/useBreakpoint';
import SentryService from '@/services/sentry/Sentry.service';
import { IsDesktopProps } from '@/shared/styles/types';
import { ROUTES } from '@/shared/constants/routes';

enum Panel {
  LIST = 'LIST',
  CONVERSATION = 'CONVERSATION',
  SETTINGS = 'SETTINGS',
}

export default function MessageInbox() {
  const router = useRouter();
  const { isMobile, isDesktop } = useBreakpoint();
  const connectionState = useConnectionState();
  const isSendbirdConnected = connectionState === ConnectionState.OPEN;

  const {
    state: {
      stores: {
        sdkStore: { sdk },
      },
    },
  } = useSendbird();

  const { channelUrl: queryChannelUrl } = useGlobalSearchParams<{
    channelUrl?: string;
  }>();

  const [panel, setPanel] = useState<Panel>(Panel.LIST);
  const [currentChannelUrl, setCurrentChannelUrl] = useState<string>();
  const [hasChannels, setHasChannels] = useState(false);

  // More reliable connection check: if we have SDK and can query channels, we're connected
  const isActuallyConnected = Boolean(
    sdk?.groupChannel &&
      (isSendbirdConnected || hasChannels || currentChannelUrl),
  );

  const showError = !isActuallyConnected && (isDesktop || panel === Panel.LIST);

  useEffect(() => {
    if (queryChannelUrl && queryChannelUrl !== currentChannelUrl) {
      setCurrentChannelUrl(queryChannelUrl);
      setPanel(Panel.CONVERSATION);
    }
  }, [queryChannelUrl, currentChannelUrl]);

  useEffect(() => {
    if (
      isMobile ||
      queryChannelUrl ||
      !sdk?.groupChannel?.createMyGroupChannelListQuery
    ) {
      return;
    }

    (async () => {
      try {
        const query = sdk.groupChannel.createMyGroupChannelListQuery({
          order: GroupChannelListOrder.LATEST_LAST_MESSAGE,
          includeEmpty: true,
          limit: 1,
        });
        const channels = await query.next();
        if (channels.length > 0) {
          const url = channels[0].url;
          setCurrentChannelUrl(url);
          setHasChannels(true);
          router.setParams({ channelUrl: url });
        }
      } catch (error) {
        SentryService.captureError('Failed to fetch initial channel', error);
      }
    })();
  }, [sdk, isMobile, queryChannelUrl, router]);

  if (showError) {
    return (
      <ErrorContainer>
        <StyledH3>Oops!</StyledH3>
        <StyledBodyText>
          There was an error retrieving your messages. Please refresh the page
          or{' '}
          <SupportLink
            onPress={() => Linking.openURL(`mailto:${EMME_SUPPORT_EMAIL}`)}
          >
            contact support
          </SupportLink>{' '}
          if the issue persists.
        </StyledBodyText>
      </ErrorContainer>
    );
  }

  // --- DESKTOP ---
  if (!isMobile) {
    return (
      <Container className="sendbird-app">
        <AppWrap className="sendbird-app__wrap">
          <ChannelListWrapper className="sendbird-app__channellist-wrap">
            <ChannelList
              selectedChannelUrl={currentChannelUrl}
              onChannelSelect={(channel) => {
                if (!channel) return;
                setCurrentChannelUrl(channel.url);
                router.setParams({ channelUrl: channel.url });
              }}
              onChannelCreated={(channel) => {
                setCurrentChannelUrl(channel?.url);
                router.setParams({ channelUrl: channel?.url });
              }}
            />
          </ChannelListWrapper>

          <ConversationWrap
            className="sendbird-app__conversation-wrap"
            isDesktop={isDesktop}
          >
            <Channel
              channelUrl={currentChannelUrl ?? ''}
              onChatHeaderActionClick={() => setPanel(Panel.SETTINGS)}
            />
          </ConversationWrap>

          {panel === Panel.SETTINGS && currentChannelUrl && (
            <SettingsPane>
              <ChannelSettings
                channelUrl={currentChannelUrl}
                onCloseClick={() => setPanel(Panel.CONVERSATION)}
              />
            </SettingsPane>
          )}
        </AppWrap>
      </Container>
    );
  }

  // --- MOBILE ---
  return (
    <MobileContainer>
      {panel === Panel.LIST && (
        <ChannelListWrapper className="sendbird-app__channellist-wrap">
          <ChannelList
            disableAutoSelect={true}
            onChannelSelect={(channel) => {
              if (channel && channel.url) {
                setCurrentChannelUrl(channel.url);
                setPanel(Panel.CONVERSATION);
                router.setParams({ channelUrl: channel.url });
              }
            }}
            onChannelCreated={(channel) => {
              setCurrentChannelUrl(channel.url);
              setPanel(Panel.CONVERSATION);
              router.setParams({ channelUrl: channel.url });
            }}
          />
        </ChannelListWrapper>
      )}

      {panel === Panel.CONVERSATION && currentChannelUrl && (
        <ConversationWrap isDesktop={isDesktop}>
          <Channel
            channelUrl={currentChannelUrl}
            onBackClick={() => {
              setPanel(Panel.LIST);
              router.navigate(ROUTES.MESSAGES.path);
            }}
            onChatHeaderActionClick={() => setPanel(Panel.SETTINGS)}
          />
        </ConversationWrap>
      )}

      {panel === Panel.SETTINGS && currentChannelUrl && (
        <ChannelSettings
          channelUrl={currentChannelUrl}
          onCloseClick={() => setPanel(Panel.CONVERSATION)}
        />
      )}
    </MobileContainer>
  );
}

const Container = styled.div({
  display: 'flex',
  flex: 1,
  marginBottom: 48,
});

const MobileContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
});

const ChannelListWrapper = styled.div({
  border: 'none !important',
  height: '100%',
  marginBottom: 24,

  '& > .sendbird-channel-list': {
    borderRight: `1px solid ${lightGrey}`,
    '& > .sendbird-channel-list__header': {
      height: 0,
      minHeight: 0,
    },
  },
});

const ConversationWrap = styled.div<IsDesktopProps>(({ isDesktop }) => ({
  height: '100%',
  ...(!isDesktop && {
    backgroundColor: sand,
    padding: '0 14px 14px',
  }),

  '& > .sendbird-conversation ': {
    border: 'none',
    ...(!isDesktop && {
      backgroundColor: 'transparent',
    }),

    '& > .sendbird-conversation__channel-header': {
      ...(!isDesktop && {
        backgroundColor: sand,
        borderBottom: 'none',
        padding: '0 0 0 4px',
      }),
    },

    '& > .sendbird-conversation__messages': {
      ...(!isDesktop && {
        borderTopRightRadius: 16,
        borderTopLeftRadius: 16,
        backgroundColor: white,
      }),
    },

    '& > .sendbird-conversation__footer': {
      minWidth: 0,
      overflow: 'hidden',
      '& .sendbird-message-input-wrapper': {
        maxWidth: '100%',
        minWidth: 0,
      },
      ...(!isDesktop && {
        backgroundColor: white,
        borderBottomRightRadius: 16,
        borderBottomLeftRadius: 16,
        marginTop: 0,
        paddingTop: 6,
      }),
    },
  },
}));

const AppWrap = styled.div({
  border: `1px solid ${lightGrey}`,
});

const SettingsPane = styled.div({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  borderLeft: `1px solid ${lightGrey}`,
  background: 'white',
  zIndex: 1,
});
